import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaEdit, FaTrash, FaFilePdf, FaSearch, FaDownload, FaSmile, FaMeh, FaFrown, FaUsers, FaTrophy } from "react-icons/fa";
import ConfirmModal from "../Modals/ConfirmModal";
import ProgressModal from "../ProgressModal";
import Dropdown from "../DropDown";
import { useBoletin } from "../../context/BoletinContext";
import { useUsers } from "../../context/UsersContext";
import { useNotas } from "../../context/NotasContext"; // ✅ AGREGAR

const BoletinList = ({ boletines, onEdit, onGenerarPDF, showNotification }) => {
  const { eliminarBoletin, fetchBoletinesByDirector, generarBoletin, generarPuestosPeriodo, validarNotasAntesDeGenerar, validarNotasSalonAntesDeGenerar, progreso } = useBoletin();
  const { salonGrado } = useUsers();
  const { periodos } = useNotas();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBoletin, setSelectedBoletin] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedPeriodo, setSelectedPeriodo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const boletinesPorPagina = 5;

  const [filteredBoletines, setFilteredBoletines] = useState([]);

  // Estados para generar múltiples boletines
  const [showGenerarTodosModal, setShowGenerarTodosModal] = useState(false);
  const [showConfirmGenerarModal, setShowConfirmGenerarModal] = useState(false);
  const [periodoSeleccionado, setPeriodoSeleccionado] = useState("");
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);
  const [boletinesPeriodo, setBoletinesPeriodo] = useState([]);

  // NUEVOS ESTADOS para generar puestos
  const [showGenerarPuestosModal, setShowGenerarPuestosModal] = useState(false);
  const [showConfirmPuestosModal, setShowConfirmPuestosModal] = useState(false);
  const [periodoSeleccionadoPuestos, setPeriodoSeleccionadoPuestos] = useState("");
  const [isGeneratingPuestos, setIsGeneratingPuestos] = useState(false);

  const grado = salonGrado?.salon_grado?.toLowerCase();
  const esPreescolar = ["parvulos", "prejardin", "jardin", "transicion"].includes(grado);

  useEffect(() => {
    setFilteredBoletines(boletines);
  }, [boletines]);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchValue(value);
    filtrarBoletines(value, selectedPeriodo);
  };

  const handlePeriodoChange = (e) => {
    setSelectedPeriodo(e.target.value);
    filtrarBoletines(searchValue, e.target.value);
  };

  const filtrarBoletines = (busqueda, periodo) => {
    const filtrados = boletines.filter((b) => {
      const nombre = b.nombre_estudiante?.toLowerCase() || "";
      const coincideNombre = nombre.includes(busqueda);
      const coincidePeriodo = periodo === "" || b.periodo_nombre === periodo;
      return coincideNombre && coincidePeriodo;
    });
    setFilteredBoletines(filtrados);
    setCurrentPage(1);
  };

  const indexOfLast = currentPage * boletinesPorPagina;
  const indexOfFirst = indexOfLast - boletinesPorPagina;
  const currentBoletines = filteredBoletines.slice(indexOfFirst, indexOfLast);
  const totalPaginas = Math.ceil(filteredBoletines.length / boletinesPorPagina);

  const openDeleteModal = (boletin) => {
    setSelectedBoletin(boletin);
    setIsModalOpen(true);
  };

  const closeDeleteModal = () => {
    setSelectedBoletin(null);
    setIsModalOpen(false);
  };

  const handleDelete = async () => {
    if (selectedBoletin) {
      setIsLoading(true);
      try {
        await eliminarBoletin(
          selectedBoletin.students_id,
          selectedBoletin.periodo_id
        );
        showNotification({
          type: "success",
          message: "Boletín eliminado correctamente.",
        });
        await fetchBoletinesByDirector(true);
        closeDeleteModal();
      } catch (error) {
        showNotification({
          type: "error",
          message: "Error al eliminar boletín.",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleDownloadPDF = async (boletin) => {
    try {
      const pdfUrl = boletin?.pdf_url;
      if (!pdfUrl) {
        showNotification({
          type: "error",
          message: "Este boletín no tiene un archivo PDF disponible.",
        });
        return;
      }
  
      const fileResponse = await axios.get(pdfUrl, { responseType: "blob" });
      const blob = new Blob([fileResponse.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
  
      const decodedUrl = decodeURIComponent(pdfUrl);
      const parts = decodedUrl.split("/");
      let rawFilename = parts[parts.length - 1];
  
      if (!rawFilename.endsWith(".pdf")) {
        rawFilename += ".pdf";
      }
  
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", rawFilename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showNotification({
        type: "error",
        message: "No se pudo descargar el boletín.",
      });
    }
  };

  // VALIDAR Y MOSTRAR MODAL DE CONFIRMACIÓN (Boletines)
  const handlePrepararGeneracion = () => {
    if (!periodoSeleccionado) {
      showNotification({
        type: "error",
        message: "Por favor seleccione un periodo",
      });
      return;
    }

    const boletinesFiltrados = boletines.filter(
      (b) => b.periodo_nombre === periodoSeleccionado
    );

    if (boletinesFiltrados.length === 0) {
      showNotification({
        type: "error",
        message: "No hay boletines para generar en este periodo",
      });
      return;
    }

    setBoletinesPeriodo(boletinesFiltrados);
    setShowGenerarTodosModal(false);
    setShowConfirmGenerarModal(true);
  };

  // EJECUTAR GENERACIÓN MASIVA (Boletines)
  const handleGenerarTodos = async () => {
    setShowConfirmGenerarModal(false);
    setIsGeneratingAll(true);
    setShowProgressModal(true);

    let exitosos = 0;
    let fallidos = 0;

    for (let i = 0; i < boletinesPeriodo.length; i++) {
      const boletin = boletinesPeriodo[i];
      
      try {
        await generarBoletin(
          boletin.students_id,
          boletin.periodo_id,
          boletin.grado
        );
        exitosos++;
      } catch (error) {
        console.error(`Error generando boletín de ${boletin.nombre_estudiante}:`, error);
        fallidos++;
      }

      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    setIsGeneratingAll(false);
    setShowProgressModal(false);

    showNotification({
      type: exitosos > 0 ? "success" : "error",
      message: `${exitosos} boletines generados correctamente${fallidos > 0 ? ` | ❌ ${fallidos} fallidos` : ""}`,
    });

    await fetchBoletinesByDirector(true);
    setPeriodoSeleccionado("");
  };

  // NUEVAS FUNCIONES PARA GENERAR PUESTOS
  const handlePrepararGeneracionPuestos = () => {
    if (!periodoSeleccionadoPuestos) {
      showNotification({
        type: "error",
        message: "Por favor seleccione un periodo",
      });
      return;
    }

    setShowGenerarPuestosModal(false);
    setShowConfirmPuestosModal(true);
  };

  const handleGenerarPuestos = async () => {
    if (!periodoSeleccionadoPuestos) {
      showNotification("error", "Debe seleccionar un periodo");
      return;
    }

    setIsGeneratingPuestos(true);

    try {
      // 🆕 Validar que todos los estudiantes tengan notas completas
      const validacion = await validarNotasSalonAntesDeGenerar(periodoSeleccionadoPuestos);

      if (!validacion.completo) {
        showNotification("error", validacion.mensaje);
        
        // 🆕 MOSTRAR MODAL DETALLADO CON LA LISTA DE ESTUDIANTES
        if (validacion.estudiantesSinNotas && validacion.estudiantesSinNotas.length > 0) {
          console.log("Estudiantes sin notas completas:", validacion.estudiantesSinNotas);
          const detallesHTML = validacion.estudiantesSinNotas.map(e => 
            `📌 ${e.estudiante}:\n   • Total asignaturas: ${e.totalAsignaturas}\n   • Notas registradas: ${e.notasRegistradas}\n   • Faltan: ${e.asignaturasFaltantes.join(', ')}`
          ).join('\n\n');
          
          alert(`⚠️ ESTUDIANTES CON NOTAS INCOMPLETAS\n\n${detallesHTML}\n\n💡 Complete las notas faltantes antes de generar los puestos.`);
        }
        
        setIsGeneratingPuestos(false);
        setShowConfirmPuestosModal(false);
        return;
      }

      // ✅ CORREGIR: Buscar el ID del periodo por nombre
      const periodo = periodosDisponibles.find(p => p.nombre === periodoSeleccionadoPuestos);
      const periodoId = periodo ? periodo.id : null;

      if (!periodoId) {
        showNotification("error", "No se encontró el periodo seleccionado");
        return;
      }
      
      const response = await generarPuestosPeriodo(periodoId);

      showNotification({
        type: "success",
        message: `Puestos generados correctamente. PDF disponible.`,
      });

      // Descargar automáticamente el PDF
      if (response.pdfUrl) {
        window.open(response.pdfUrl, '_blank');
      }

    } catch (error) {
      showNotification({
        type: "error",
        message: error.message || "Error al generar puestos",
      });
    } finally {
      setIsGeneratingPuestos(false);
      setPeriodoSeleccionadoPuestos("");
    }
  };

  const renderCarita = (valor) => {
    switch (valor) {
      case "feliz":
        return <FaSmile className="text-green text-2xl mx-auto" />;
      case "contento":
        return <FaSmile className="text-main-blue text-2xl mx-auto" />;
      case "intermedio":
        return <FaMeh className="text-yellow-500 text-2xl mx-auto" />;
      case "triste":
        return <FaFrown className="text-red-500 text-2xl mx-auto" />;
      default:
        return <span className="text-gray-400">-</span>;
    }
  };

  // ✅ USAR DIRECTAMENTE LOS PERIODOS DEL CONTEXT:
  const periodosDisponibles = periodos || [];

  return (
    <div className="bg-white p-6 rounded-lg shadow-md mt-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold">Boletines Registrados</h3>
        
        <div className="flex gap-3">
          {/* NUEVO BOTÓN: Generar Puestos (solo para primaria) */}
          {!esPreescolar && (
            <button
              onClick={() => setShowGenerarPuestosModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
            >
              <FaTrophy size={16} />
              Generar Puestos
            </button>
          )}

          <button
            onClick={() => setShowGenerarTodosModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            <FaUsers size={16} />
            Generar Boletines
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="flex items-center border border-gray-300 rounded-full px-4 py-2">
          <FaSearch className="mr-2" />
          <input
            type="text"
            placeholder="Buscar por nombre..."
            value={searchValue}
            onChange={handleSearch}
            className="outline-none w-full"
          />
        </div>

        <select
          value={selectedPeriodo}
          onChange={handlePeriodoChange}
          className="border border-gray-300 rounded-full px-4 py-2 w-full"
        >
          <option value="">Todos los periodos</option>
          {periodosDisponibles
            .filter(p => p.id <= 4) // Solo periodos 1-4
            .map((p) => (
              <option key={p.id} value={p.nombre}>
                {p.nombre}
              </option>
            ))}
        </select>
      </div>

      {filteredBoletines.length === 0 ? (
        <p>No hay boletines registrados.</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg shadow-md">
            <table className="table-auto w-full border-collapse border border-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-4 py-2 border text-center">Estudiante</th>
                  <th className="px-4 py-2 border text-center">Periodo</th>
                  {esPreescolar && (
                    <>
                      <th className="px-4 py-2 border text-center">Cumplimiento Tareas</th>
                      <th className="px-4 py-2 border text-center">Comportamiento</th>
                    </>
                  )}
                  <th className="px-4 py-2 border text-center">Inasistencias</th>
                  <th className="px-4 py-2 border text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {currentBoletines.map((boletin) => (
                  <tr key={`${boletin.students_id}-${boletin.periodo_id}`}>
                    <td className="px-4 py-2 border text-center">
                      {boletin.nombre_estudiante}
                    </td>
                    <td className="px-4 py-2 border text-center">
                      {boletin.periodo_nombre}
                    </td>
                    {esPreescolar && (
                      <>
                        <td className="px-4 py-2 border text-center">
                          {renderCarita(boletin.cumplimiento_tareas)}
                        </td>
                        <td className="px-4 py-2 border text-center">
                          {renderCarita(boletin.comportamiento_valores)}
                        </td>
                      </>
                    )}
                    <td className="px-4 py-2 border text-center">
                      {boletin.inasistencias}
                    </td>
                    <td className="px-4 py-2 border text-center">
                      <div className="flex justify-center gap-4">
                        <button
                          onClick={() => onEdit(boletin)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Editar"
                        >
                          <FaEdit size={25} />
                        </button>
                        <button
                          onClick={() => openDeleteModal(boletin)}
                          className="text-red-600 hover:text-red-800"
                          title="Eliminar"
                        >
                          <FaTrash size={25} />
                        </button>
                        <button
                          onClick={() => onGenerarPDF(boletin.students_id, boletin.periodo_id, boletin.grado)}
                          className="text-green-600 hover:text-green-800"
                          title="Generar PDF"
                        >
                          <FaFilePdf size={25} />
                        </button>
                        {boletin.pdf_url && (
                          <button
                            onClick={() => handleDownloadPDF(boletin)}
                            className="text-purple-600 hover:text-purple-800"
                            title="Descargar PDF"
                          >
                            <FaDownload size={25} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end items-center mt-4 space-x-4">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="w-10 h-10 flex justify-center items-center rounded-full text-gray hover:bg-gray-200 disabled:opacity-50"
            >
              ◀
            </button>

            {[...Array(totalPaginas)]
              .map((_, i) => i + 1)
              .filter((page) => page >= currentPage - 1 && page <= currentPage + 1)
              .map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-10 h-10 flex justify-center items-center border-2 border-gray text-gray rounded-full ${
                    currentPage === page
                      ? "bg-main-blue text-white shadow-lg"
                      : "hover:bg-gray-200 hover:text-black hover:border-black"
                  }`}
                >
                  {page}
                </button>
              ))}

            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPaginas))}
              disabled={currentPage === totalPaginas}
              className="w-10 h-10 flex justify-center items-center rounded-full text-gray hover:bg-gray-200 disabled:opacity-50"
            >
              ▶
            </button>
          </div>
        </>
      )}

      {/* Modal de Confirmación de Eliminación */}
      <ConfirmModal
        isOpen={isModalOpen}
        title="Confirmar Eliminación"
        message="¿Estás seguro de que deseas eliminar este boletín? Esta acción no se puede deshacer."
        onConfirm={handleDelete}
        onCancel={closeDeleteModal}
        isLoading={isLoading}
      />

      {/* MODAL 1: Seleccionar Periodo (Boletines) */}
      {showGenerarTodosModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">Generar Boletines Masivamente</h3>
            
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2 text-gray-700">
                Seleccione el Periodo
              </label>
              <Dropdown
                value={periodoSeleccionado}
                onChange={(e) => setPeriodoSeleccionado(e.target.value)}
                options={[
                  { value: "", label: "Seleccione un periodo" },
                  ...periodosDisponibles
                    .filter(p => p.id <= 4) // Solo periodos 1-4
                    .map((p) => ({ value: p.nombre, label: p.nombre })),
                ]}
                placeholder="Seleccione un periodo"
              />
            </div>

            <div className="p-3 bg-blue-50 rounded-lg mb-4 border-l-4 border-blue-500">
              <p className="text-sm text-blue-800">
                ℹ️ Se generarán todos los boletines del periodo seleccionado para los estudiantes de su salón.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowGenerarTodosModal(false);
                  setPeriodoSeleccionado("");
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition"
              >
                Cancelar
              </button>
              <button
                onClick={handlePrepararGeneracion}
                disabled={!periodoSeleccionado}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NUEVO MODAL: Seleccionar Periodo (Puestos) */}
      {showGenerarPuestosModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">🏆 Generar Tabla de Puestos</h3>
            
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2 text-gray-700">
                Seleccione el Periodo
              </label>
              <Dropdown
                value={periodoSeleccionadoPuestos}
                onChange={(e) => setPeriodoSeleccionadoPuestos(e.target.value)}
                options={[
                  { value: "", label: "Seleccione un periodo" },
                  ...periodosDisponibles
                    .filter(p => p.id <= 4) // Solo periodos 1-4
                    .map((p) => ({ value: p.nombre, label: p.nombre })),
                ]}
                placeholder="Seleccione un periodo"
              />
            </div>

            <div className="p-3 bg-yellow-50 rounded-lg mb-4 border-l-4 border-yellow-500">
              <p className="text-sm text-yellow-800">
                📊 Se calcularán los promedios y puestos de todos los estudiantes del salón según sus **notas** del periodo.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowGenerarPuestosModal(false);
                  setPeriodoSeleccionadoPuestos("");
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition"
              >
                Cancelar
              </button>
              <button
                onClick={handlePrepararGeneracionPuestos}
                disabled={!periodoSeleccionadoPuestos}
                className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 2: Confirmación Final (Boletines) */}
      <ConfirmModal
        isOpen={showConfirmGenerarModal}
        title="⚠️ Confirmar Generación Masiva Boletines"
        message={`¿Está seguro de generar ${boletinesPeriodo.length} boletines del periodo "${periodoSeleccionado}"?\n\n⏱️ Esta operación puede tomar varios minutos.\n\nSe generarán todos los PDF automáticamente.`}
        onConfirm={handleGenerarTodos}
        onCancel={() => {
          setShowConfirmGenerarModal(false);
          setPeriodoSeleccionado("");
        }}
        isLoading={isGeneratingAll}
      />

      {/* NUEVO MODAL: Confirmación Final (Puestos) */}
      <ConfirmModal
        isOpen={showConfirmPuestosModal}
        title="🏆 Confirmar Generación de Puestos"
        message={`¿Desea generar la tabla de puestos del periodo "${periodoSeleccionadoPuestos}"?\n\n📊 Se calcularán los promedios con base únicamente en las **notas** registradas.\n\nSe generará un PDF con la tabla ordenada.`}
        onConfirm={handleGenerarPuestos}
        onCancel={() => {
          setShowConfirmPuestosModal(false);
          setPeriodoSeleccionadoPuestos("");
        }}
        isLoading={isGeneratingPuestos}
      />

      {/* Modal de Progreso */}
      <ProgressModal
        isOpen={showProgressModal}
        progreso={progreso}
        onClose={() => setShowProgressModal(false)}
      />
    </div>
  );
};

export default BoletinList;